import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { RotateCcw, X } from 'lucide-react';
import { ProfileFilters } from '@/hooks/useProfileFilters';
import { ProfileData } from '@/types/profile';
import { motion, AnimatePresence } from 'framer-motion';
import { useFilteredResultsPreview } from '@/hooks/useFilteredResultsPreview';
import FilterCategoryList from './FilterCategoryList';
import CategoryOptionsModal from './CategoryOptionsModal';
import { toast } from 'sonner';

interface HierarchicalFilterModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: ProfileFilters;
  onUpdateFilter: (key: keyof ProfileFilters, value: any) => void;
  onClearFilters: () => void;
  availableInterests: string[];
  rawProfiles: ProfileData[];
  userRole?: string;
  userOrientation?: string;
}

export type FilterCategory = 
  | 'gender'
  | 'orientation' 
  | 'relationshipTypes'
  | 'estadoCivil'
  | 'alturas'
  | 'tiposCorpo'
  | 'coresOlhos'
  | 'coresCabelo'
  | 'fumante'
  | 'bebidas'
  | 'tatuagens'
  | 'piercings'
  | 'interests';

export const HierarchicalFilterModal: React.FC<HierarchicalFilterModalProps> = ({
  isOpen,
  onOpenChange,
  filters,
  onUpdateFilter,
  onClearFilters,
  availableInterests,
  rawProfiles,
  userRole,
  userOrientation
}) => {
  const [activeModal, setActiveModal] = useState<'main' | FilterCategory>('main');
  
  // Real-time results preview
  const { count: resultCount, isEmpty, statusColor } = useFilteredResultsPreview(
    rawProfiles,
    filters,
    userRole,
    userOrientation
  );

  const getTotalFilterCount = (): number => {
    let count = 0;
    
    // Contar filtros em arrays
    const arrayFilters: (keyof ProfileFilters)[] = [
      'gender', 'orientation', 'relationshipTypes', 'estadoCivil',
      'alturas', 'tiposCorpo', 'coresOlhos', 'coresCabelo',
      'fumante', 'bebidas', 'tatuagens', 'piercings', 'interests'
    ];
    
    arrayFilters.forEach(key => {
      const value = filters[key];
      if (Array.isArray(value)) {
        count += value.length;
      }
    });
    
    return count;
  };

  const handleCategoryClick = (category: FilterCategory) => {
    setActiveModal(category);
  };

  const handleBackToMain = () => {
    setActiveModal('main');
  };

  const handleClose = () => {
    const count = getTotalFilterCount();
    if (count > 0) {
      toast.success(`${count} filtro${count > 1 ? 's' : ''} aplicado${count > 1 ? 's' : ''} • ${resultCount} resultado${resultCount !== 1 ? 's' : ''}`);
    }
    setActiveModal('main');
    onOpenChange(false);
  };

  const slideVariants = {
    main: {
      x: 0,
      opacity: 1,
    },
    slideOutLeft: {
      x: '-100%',
      opacity: 0,
    },
    slideInRight: {
      x: '100%',
      opacity: 0,
    },
    slideInFromRight: {
      x: 0,
      opacity: 1,
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-md bg-black/95 border-white/10 overflow-hidden p-0"
        hasTitle={true}
      >
        <div className="relative h-full flex flex-col">
          {/* Área animada com header + conteúdo */}
          <div className="flex-1 overflow-hidden">
            <AnimatePresence mode="wait">
              {activeModal === 'main' ? (
                <motion.div
                  key="main"
                  initial={{ x: 0, opacity: 1 }}
                  animate="main"
                  exit="slideOutLeft"
                  variants={slideVariants}
                  transition={{ type: "tween", duration: 0.3 }}
                  className="h-full flex flex-col"
                >
                  <SheetHeader className="p-4 border-b border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <SheetTitle className="text-white">Filtros</SheetTitle>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor} bg-white/5 border border-current/20`}>
                          {resultCount} {resultCount === 1 ? 'resultado' : 'resultados'}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClearFilters}
                        className="text-white/70 hover:text-white hover:bg-white/10"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Limpar
                      </Button>
                    </div>
                  </SheetHeader>

                  <div className="flex-1 overflow-y-auto">
                    <FilterCategoryList
                      filters={filters}
                      onUpdateFilter={onUpdateFilter}
                      onCategoryClick={handleCategoryClick}
                      availableInterests={availableInterests}
                    />
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key={activeModal}
                  initial="slideInRight"
                  animate="slideInFromRight"
                  exit="slideInRight"
                  variants={slideVariants}
                  transition={{ type: "tween", duration: 0.3 }}
                  className="h-full"
                >
                  <CategoryOptionsModal
                    category={activeModal}
                    filters={filters}
                    onUpdateFilter={onUpdateFilter}
                    onBack={handleBackToMain}
                    availableInterests={availableInterests}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Rodapé fixo - sempre visível */}
          <div className="p-4 border-t border-white/10 bg-black/95">
            <div className="flex gap-2">
              <Button 
                variant="ghost"
                onClick={handleClose}
                className="flex-shrink-0 text-white/70 hover:text-white hover:bg-white/10 p-2"
              >
                <X className="h-4 w-4" />
              </Button>
              <Button 
                onClick={handleClose}
                className={`flex-1 ${isEmpty ? 'bg-red-500/80 hover:bg-red-500/70' : 'bg-luxury-red hover:bg-luxury-red/90'}`}
                disabled={isEmpty}
              >
                {getTotalFilterCount() > 0 
                  ? `Aplicar ${getTotalFilterCount()} ${getTotalFilterCount() === 1 ? 'Filtro' : 'Filtros'} - ${resultCount} ${resultCount === 1 ? 'Resultado' : 'Resultados'}`
                  : `Ver Resultados (${resultCount})`
                }
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default HierarchicalFilterModal;
